import styled from "@emotion/styled";
import msk from "msk";
import React, { useEffect, useState } from "react";
import { Country } from "../types/CountryTypes";
import countries from "../utils/countries.json";
import CountryFinderInput from "./CountryFinder";

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const InputContainer = styled.div`
  &:last-child {
    margin-top: 10px;
  }
  width: 100%;
`;

const PhoneInputContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CountryCodeInput = styled.input`
  width: 15%;
  padding: 5px 0px 5px 5px;
  height: 35px;
  font-size: 14px;
  appearance: none;
  border: 1px solid #dddddd;
  border-radius: 5px 0px 0px 5px;
  border-right: none;
  color: #2f2f2f;
  :focus {
    outline: none;
  }
`;
const PhoneTextInput = styled.input`
  width: 85%;
  padding: 5px 5px 5px 0;
  height: 35px;
  font-size: 14px;
  border: 1px solid #dddddd;
  border-radius: 0px 5px 5px 0px;
  color: #2f2f2f;
  border-left: none;
  appearance: none;
  :focus {
    outline: none;
  }
  ::placeholder {
    opacity: 50%;
  }
`;

function getMask(countryCode: string) {
  let result = "(999) 999-9999";
  switch (countryCode) {
    case "+1":
      result = "(999) 999-9999";
      break;
    case "+55":
      result = "99 9 9999-9999";
      break;
    case "+49":
      result = "99 999 999 999";
      break;
    default:
      result = "999999999999";
      break;
  }
  return result;
}

const ONLY_NUMBERS_REGEX = /\d+/g;

interface Props {
  countryCode: string;
  handleCountryCode: (vle: string) => void;
  phone: string | undefined;
  handlePhone: (vle: string) => void;
}

export default function PhoneInput(props: Props): JSX.Element {
  const { countryCode, handleCountryCode, phone, handlePhone } = props;
  const [nextEnabled, setNextEnabled] = useState(false);
  // const history = useHistory();

  /**
   * Handles the country code changing both from inside the CountryFinderInput
   * and the input with the "+99..."
   *
   * If the change comes from inside the CountryFinderInput it ignores
   * the "hit country" check and just overwrites it
   *
   * @param code
   * @param force
   */
  function handleCountryCodeChange(code: string, options?: { force: boolean }) {
    // If the string is getting bigger, see if it hit a country
    // as countries have codes that are not inclusive (eg: if it
    // exists a +1 there can't be a +12), hitting a country means
    // we stop accepting more numbers into the input
    if (!options?.force && code.length > countryCode.length) {
      const country = countries.find(
        (elem: { code: string }) => elem.code === `${countryCode}`
      );

      if (`${countryCode}` === country?.code) return;
    }

    const value = msk.fit(code, "9999");
    handleCountryCode(`+${value}`);
  }

  function handlePhoneChange(event: React.ChangeEvent<HTMLInputElement>) {
    const value = msk.fit(event.target.value, getMask(countryCode));

    handlePhone(value);
  }

  useEffect(() => {
    if (!phone) return;
    if (phone.length < 6) return;

    const phoneOnlyNumbers = phone.match(ONLY_NUMBERS_REGEX)?.join("");
    const parsedCountryCode = countryCode.split("+")[1];

    const fullPhone = `${parsedCountryCode}${phoneOnlyNumbers}`;

    const controller = new AbortController();
    const { signal } = controller;

    // eslint-disable-next-line consistent-return
    return () => {
      controller.abort();
    };
  }, [phone, countryCode]);

  function next() {
    if (!nextEnabled) return;

    let sendPhone = countryCode.replace("+", "");

    sendPhone += phone
      ?.replace("(", "")
      .replace(")", "")
      .replace("-", "")
      .split(" ")
      .join("");
  }

  function handlePhoneOnkeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      next();
    }
  }

  return (
    <Container>
      <InputContainer>
        <CountryFinderInput
          countryCode={countryCode}
          setCountryCode={() => {}}
          handleChangeCountry={(
            country: any,
            options: { force: boolean } | undefined
          ) => handleCountryCodeChange((country as Country).code, options)}
          style={{
            height: "40px",
            borderColor: "#eceeef",
            fontSize: "16px",
          }}
        />
      </InputContainer>
      <InputContainer>
        <PhoneInputContainer>
          <CountryCodeInput
            onChange={(event) => handleCountryCodeChange(event.target.value)}
            value={countryCode}
            onKeyDown={handlePhoneOnkeyDown}
            inputMode="tel"
            pattern="[0-9]*"
          />
          <PhoneTextInput
            onChange={handlePhoneChange}
            value={phone}
            onKeyDown={handlePhoneOnkeyDown}
            placeholder={getMask(countryCode)}
            pattern="[0-9]*"
            inputMode="tel"
          />
        </PhoneInputContainer>
      </InputContainer>
    </Container>
  );
}
